import React from 'react';
import { FormattedMessage, FormattedDate } from '../../util/reactIntl';
import { DATE_TYPE_DATE, LINE_ITEM_HOUR, LINE_ITEM_NIGHT } from '../../util/types';
import { subtractTime } from '../../util/dates';

import css from './OrderBreakdown.module.css';
import { getDurationText, getListingAddress } from '../../util/genericHelpers';
import Icons from '../Icons/Icons';
import classNames from 'classnames';
import AddressLinkMaybe from '../../containers/TransactionPage/TransactionPanel/AddressLinkMaybe';
import { canShowBookingLocation } from '../../util/bookingLocation';

const BookingPeriod = props => {
  const {
    startDate,
    endDate,
    dateType,
    timeZone,
    durationType,
    transaction,
    isListingPage,
    isCheckoutPage,
    listing,
    locale,
  } = props;
  const timeZoneMaybe = timeZone ? { timeZone } : null;

  const durationText = getDurationText(durationType, locale);

  const timeFormatOptions =
    dateType === DATE_TYPE_DATE
      ? {
          weekday: 'long',
        }
      : {
          // weekday: 'short',
          hour: 'numeric',
          minute: 'numeric',
        };

  const dateFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  const showLocation = canShowBookingLocation(transaction);
  const thLanguage = listing?.attributes?.publicData?.thLanguage;
  const { address, location } = getListingAddress(listing, thLanguage);
  const locationForAddressLink = location || (address ? { address } : null);
  return (
    <>
      <div className={css.bookingPeriod}>
        <div className={css.bookingPeriodSectionLeft}>
          {isListingPage || isCheckoutPage ? (
            <div className={css.itemLabel}>
              <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
            </div>
          ) : (
            <div className={css.bookingInformation}>
              <Icons name="calendarOutline" />
              <div className={css.bookingInfo}>
                <h2>
                  <FormattedMessage
                    id="LineItemBookingPeriod.bookingDate"
                    defaultMessage={'Booking Date'}
                  />
                </h2>
                <div className={css.itemLabel}>
                  <FormattedDate value={startDate} {...dateFormatOptions} {...timeZoneMaybe} />
                </div>
              </div>
            </div>
          )}

          {isListingPage || isCheckoutPage ? (
            <div className={css.dayInfo}>
              {durationText}{' '}
              <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} /> -{' '}
              <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
            </div>
          ) : (
            <div className={css.bookingInformation}>
              <Icons name="clock" />
              <div className={css.bookingInfo}>
                <h2>
                  <FormattedMessage
                    id="LineItemBookingPeriod.bookingTime"
                    defaultMessage={'Booking Time'}
                  />
                </h2>
                <div className={css.itemLabel}>
                  {durationText}{' '}
                  <FormattedDate value={startDate} {...timeFormatOptions} {...timeZoneMaybe} /> -{' '}
                  <FormattedDate value={endDate} {...timeFormatOptions} {...timeZoneMaybe} />
                </div>
              </div>
            </div>
          )}

          {isListingPage || isCheckoutPage || !showLocation ? null : (
            <div className={css.bookingInformation}>
              <Icons name="location" />
              <div className={css.bookingInfo}>
                <h2>
                  <FormattedMessage
                    id="LineItemBookingPeriod.bookingLocation"
                    defaultMessage={'Location'}
                  />
                </h2>

                <div className={classNames(css.itemLabel, css.addressLine)}>
                  <AddressLinkMaybe
                    linkRootClassName={css.bookingLocationAddress}
                    location={locationForAddressLink}
                    geolocation={listing?.attributes?.geolocation}
                    publicData={listing?.attributes?.publicData}
                    showAddress
                    isBookingPeriod
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/**
 * A line-item to show booking period for the OrderBreakdown
 */
const LineItemBookingPeriod = props => {
  const {
    booking,
    code,
    dateType,
    timeZone,
    durationType,
    transaction,
    overTime,
    lineItems,
    isListingPage,
    isCheckoutPage,
    listing,
    locale,
  } = props;

  if (!booking) {
    return null;
  }

  const { start, end, displayStart, displayEnd } = booking.attributes;
  const localStartDate = displayStart || start;
  const localEndDateRaw = displayEnd || end;

  const isNightly = code === LINE_ITEM_NIGHT;
  const isHour = code === LINE_ITEM_HOUR;
  const endDay = isNightly || isHour ? localEndDateRaw : subtractTime(localEndDateRaw, 1, 'days');

  return (
    <>
      <div className={css.lineItem}>
        <BookingPeriod
          startDate={localStartDate}
          endDate={endDay}
          dateType={dateType}
          timeZone={timeZone}
          durationType={durationType}
          transaction={transaction}
          overTime={overTime}
          lineItems={lineItems}
          isListingPage={isListingPage}
          isCheckoutPage={isCheckoutPage}
          listing={listing}
          locale={locale}
        />
      </div>
    </>
  );
};

export default LineItemBookingPeriod;
